import React from "react";
import { Box, Loader, LoadingOverlay } from "@mantine/core";
import { tokens } from "@blurb/codex-react";
import { GraphQL } from "@rpi/openapi-api";
import { useParams } from "react-router-dom";
import { useAuthUser } from "../../../hooks/useAuthUser";
import { Body } from "../../../components/Typography";
import { EventLogEntry } from "./eventLogs/EventLogEntry";

type OrderEvent = NonNullable<NonNullable<GraphQL.OrderEventsQuery["orderByIdentifier"]>["orderEvents"]>[number];

const getEventKey = (event: OrderEvent, index: number): string | number => event.id ?? `${event.eventTime}-${index}`;

export const OrderEventLogsTab: React.FC = () => {
  const { customerPublicId, customerOrderId } = useParams();
  const user = useAuthUser();
  const hasOrderIdentifier = !!customerPublicId && !!customerOrderId;

  const { data, isLoading, error } = GraphQL.useOrderEventsQuery(
    {
      orderIdentifier: {
        customerPublicId: customerPublicId ?? "",
        customerOrderId: customerOrderId ?? "",
      },
      isSandbox: user.isSandbox,
    },
    { enabled: hasOrderIdentifier }
  );

  const events = data?.orderByIdentifier?.orderEvents ?? [];

  if (isLoading) {
    return (
      <Box sx={{ position: "relative", minHeight: 200 }}>
        <LoadingOverlay loader={<Loader color="brand" size="xl" />} visible={true} />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ padding: tokens.spacing[4] }}>
        <Body>Failed to load event logs.</Body>
      </Box>
    );
  }

  if (events.length === 0) {
    return (
      <Box sx={{ padding: tokens.spacing[4] }}>
        <Body>No events found for this order.</Body>
      </Box>
    );
  }

  return (
    <Box sx={{ padding: tokens.spacing[4] }}>
      {events.map((event, index) => (
        <EventLogEntry
          key={getEventKey(event, index)}
          event={event}
          isLastEntry={index === events.length - 1}
          isSuperAdmin={user.is.SUPER_ADMIN}
        />
      ))}
    </Box>
  );
};
